"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import styles from "../css/login.module.css";
import api from "../../../api";

// Reusable Components
import FormInput from "../../../components/FormInput";
import Popup from "../../../components/Popup";
import Button from "../../../components/Button";

const Login = ({ switchToRegister, closePopup, onOpenFrontShop }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
    valid: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  const { login, switchRole, switchBranch } = useAuth();
  const navigate = useNavigate();

  const [pendingRoleIds, setPendingRoleIds] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [roleOptions, setRoleOptions] = useState([]); // { roleid, rolename }[]
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [switchingRole, setSwitchingRole] = useState(false);
  const [pendingRedirectUrl, setPendingRedirectUrl] = useState("");

  const [pendingBranchCodes, setPendingBranchCodes] = useState([]);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]); // { branchcode, branchname }[]
  const [selectedBranchCode, setSelectedBranchCode] = useState("");
  const [switchingBranch, setSwitchingBranch] = useState(false);

  const roleNameById = useMemo(() => {
    const map = new Map();
    (roleOptions || []).forEach((r) => {
      if (r && r.roleid != null) map.set(Number(r.roleid), r.rolename);
    });
    return map;
  }, [roleOptions]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/users/roles-detailed");
        const data = res?.data;
        setRoleOptions(Array.isArray(data) ? data : []);
      } catch (_) {
        setRoleOptions([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/users/branches");
        const data = res?.data;
        setBranchOptions(Array.isArray(data) ? data : []);
      } catch (_) {
        setBranchOptions([]);
      }
    })();
  }, []);

  const branchNameByCode = useMemo(() => {
    const map = new Map();
    (branchOptions || []).forEach((b) => {
      if (b && b.branchcode) map.set(String(b.branchcode), b.branchname || String(b.branchcode));
    });
    return map;
  }, [branchOptions]);

  const navigateByRole = (roleId, redirectUrl) => {
    const rid = Number(roleId);
    if (rid === 4) {
      navigate('/dashboard');
    } else if (rid === 5) {
      navigate('/driver');
    } else {
      navigate(redirectUrl || "/");
    }
  };

  const handleErrorPopupClose = () => {
    setShowErrorPopup(false);
    if (errorMessage && errorMessage.toLowerCase().includes("branch has been disabled")) {
      navigate("/");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setErrorMessage("Please enter both email and password.");
      setShowErrorPopup(true);
      return;
    }

    try {
      const response = await api.post("/login", formData);

      if (response.status === 200) {
        const { token, user, redirectUrl } = response.data;
        login(user, token);

        const branchCodes = Array.isArray(user?.branchcodes)
          ? user.branchcodes.map((b) => String(b))
          : (user?.branchcode ? [String(user.branchcode)] : []);
        const uniqueBranchCodes = Array.from(new Set(branchCodes.filter(Boolean)));

        const roleIds = Array.isArray(user?.roleids) ? user.roleids : [];
        const selectableRoleIds = roleIds.filter((rid) => Number(rid) !== 9);
        if (selectableRoleIds.length > 1) {
          setPendingRoleIds(selectableRoleIds);
          setSelectedRoleId(String((Number(user?.roleid) !== 9 ? user?.roleid : selectableRoleIds[0]) || selectableRoleIds[0] || ""));
          setPendingRedirectUrl(redirectUrl || "/");
          setPendingBranchCodes(uniqueBranchCodes);
          setSelectedBranchCode(String(user?.branchcode || uniqueBranchCodes[0] || ""));
          setShowRoleModal(true);
          return;
        }

        if (uniqueBranchCodes.length > 1) {
          setPendingBranchCodes(uniqueBranchCodes);
          setSelectedBranchCode(String(user?.branchcode || uniqueBranchCodes[0] || ""));
          setPendingRedirectUrl(redirectUrl || "/");
          setShowBranchModal(true);
          return;
        }

        navigateByRole(user?.roleid, redirectUrl);
      } else {
        setErrorMessage(response.data.message || "Login failed. Please try again.");
        setShowErrorPopup(true);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "An error occurred. Please try again.");
      setShowErrorPopup(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "password") {
      const length = value.length >= 8;
      const lowercase = /[a-z]/.test(value);
      const uppercase = /[A-Z]/.test(value);
      const number = /[0-9]/.test(value);
      const special = /[!@#$%^&*]/.test(value);
      const valid = length && lowercase && uppercase && number && special;
      setPasswordStrength({ length, lowercase, uppercase, number, special, valid });
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errorMessage) {
      setErrorMessage("");
      setShowErrorPopup(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleOpenFrontShop = () => {
    if (closePopup) closePopup();
    if (onOpenFrontShop) onOpenFrontShop();
  };

  return (
    <div className={styles.loginContainer}>

      {showErrorPopup && (
        <Popup
          type="error"
          message={errorMessage}
          onClose={handleErrorPopupClose}
        />
      )}

      {showRoleModal && (
        <div className={styles.loginForm}>
          <h2 className={styles.loginTitle}>Select role</h2>
          <div style={{ display: "grid", gap: 10, marginTop: "1rem" }}>
            <select
              value={selectedRoleId}
              onChange={(ev) => setSelectedRoleId(ev.target.value)}
              disabled={switchingRole}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d0d0d0",
              }}
            >
              {pendingRoleIds.map((rid) => {
                const n = Number(rid);
                const label = roleNameById.get(n) || `Role ${n}`;
                return (
                  <option key={n} value={String(n)}>
                    {label}
                  </option>
                );
              })}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: "0.5rem" }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowRoleModal(false)}
                disabled={switchingRole}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={switchingRole || !selectedRoleId}
                onClick={async () => {
                  try {
                    setSwitchingRole(true);
                    const rid = Number(selectedRoleId);
                    await switchRole(rid);

                    if (Array.isArray(pendingBranchCodes) && pendingBranchCodes.length > 1) {
                      setShowRoleModal(false);
                      setShowBranchModal(true);
                      return;
                    }

                    navigateByRole(rid, pendingRedirectUrl || "/");
                  } catch (err) {
                    const msg = err?.response?.data?.message || err?.message || "Failed to switch role";
                    setErrorMessage(msg);
                    setShowErrorPopup(true);
                  } finally {
                    setSwitchingRole(false);
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {showBranchModal && (
        <div className={styles.loginForm}>
          <h2 className={styles.loginTitle}>Select branch</h2>
          <div style={{ display: "grid", gap: 10, marginTop: "1rem" }}>
            <select
              value={selectedBranchCode}
              onChange={(ev) => setSelectedBranchCode(ev.target.value)}
              disabled={switchingBranch}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #d0d0d0",
              }}
            >
              {(pendingBranchCodes || []).map((code) => {
                const c = String(code);
                const label = branchNameByCode.get(c) || c;
                return (
                  <option key={c} value={c}>
                    {label}
                  </option>
                );
              })}
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: "0.5rem" }}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowBranchModal(false)}
                disabled={switchingBranch}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={switchingBranch || !selectedBranchCode}
                onClick={async () => {
                  try {
                    setSwitchingBranch(true);
                    await switchBranch(selectedBranchCode);
                    const rid = selectedRoleId ? Number(selectedRoleId) : undefined;
                    navigateByRole(rid, pendingRedirectUrl || "/");
                  } catch (err) {
                    const msg = err?.response?.data?.message || err?.message || "Failed to switch branch";
                    setErrorMessage(msg);
                    setShowErrorPopup(true);
                  } finally {
                    setSwitchingBranch(false);
                  }
                }}
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {!showRoleModal && !showBranchModal && (
        <div className={styles.loginForm}>

          {/* Close button anchored to the form (styled by login.module.css .closeBtn) */}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={closePopup}
            aria-label="Close login form"
          >
            ×
          </button>

          <h2 className={styles.loginTitle}>Login</h2>

          <form onSubmit={handleLogin}>

            <FormInput
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />

            <div className={styles.passwordContainer}>
              <FormInput
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className={styles.togglePassword}
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <svg
                  className={styles.eyeIcon}
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {showPassword ? (
                    <>
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </>
                  ) : (
                    <>
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                      <path d="M2 2l20 20" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            <Button type="submit" variant="primary" className={styles.loginButton}>
              Login
            </Button>
            <Button type="button" variant="approve" className={`${styles.loginButton} ${styles.frontShopButtonSmall}`} onClick={handleOpenFrontShop}>
              Front Shop
            </Button>
          </form>

          <div className={styles.authLinks}>
            <div className={styles.newProfile}>
              <button type="button" className={styles.linkButton} onClick={switchToRegister}>
                Register
              </button>
            </div>

            <div className={styles.ForgotPassword}>
              <button type="button" className={styles.linkButton}>
                Forgot Password?
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default Login;